import { FormEvent, useState } from "react";
import * as _ from "lodash";
import * as moment from "moment";
import { Alert, Button, FormControl, Well } from "react-bootstrap";
import { ILateBloodDrawFilter } from "@nkr/models/index";
import useBloodDrawsOutsideWindow from "@nkr/store/reports/hooks/use-blood-draws-outside-window";
import useExportBloodDrawsOutsideWindow from "@nkr/store/reports/hooks/use-export-blood-draws-outside-window";
import useCenterBaseList from "@nkr/store/center/hooks/use-center-base-list";
import BloodDrawDateConfirmModal from "./blood-draw-date-confirm-modal";
import Grid from "./grid";
import { styles } from "./styles";

interface IProps {
    isCenterLogin: boolean;
    centerId: number;
}

interface IPendingCorrection {
    serologyTrackingId: number;
    bloodDrawDate: string;
}

const dateFormat = "YYYY-MM-DD";
const displayDateFormat = "MM/DD/YYYY";

const LateBloodDraws = (props: IProps) => {
    const [surgeryDateFrom, setSurgeryDateFrom] = useState<string>(
        props.isCenterLogin
            ? moment().subtract(1, "month").startOf("month").format(dateFormat)
            : moment().add(1, "day").format(dateFormat),
    );
    const [surgeryDateTo, setSurgeryDateTo] = useState<string>(
        props.isCenterLogin
            ? moment().endOf("month").format(dateFormat)
            : moment().add(1, "month").endOf("month").format(dateFormat),
    );
    const [donorCenterId, setDonorCenterId] = useState<number>(0);
    const [recipientCenterId, setRecipientCenterId] = useState<number>(0);
    const [pendingCorrection, setPendingCorrection] = useState<IPendingCorrection>(null);

    const buildFilter = (): ILateBloodDrawFilter => {
        return {
            surgeryDateFrom,
            surgeryDateTo,
            centerId: props.isCenterLogin ? props.centerId : null,
            donorCenterId: donorCenterId > 0 ? donorCenterId : null,
            recipientCenterId: recipientCenterId > 0 ? recipientCenterId : null,
        };
    };

    const [filter, setFilter] = useState<ILateBloodDrawFilter>(buildFilter());

    const { list, loading, error, updateBloodDrawDate } = useBloodDrawsOutsideWindow(filter);
    const { onGridReady, exportBloodDrawsOutsideWindow } = useExportBloodDrawsOutsideWindow();
    const { list: centers } = useCenterBaseList();

    const centerOptions = _.sortBy(centers, (center) => center.alias);

    const handleSearch = () => {
        setFilter(buildFilter());
    };

    const handleBloodDrawDateSelected = (serologyTrackingId: number, bloodDrawDate: string) => {
        setPendingCorrection({ serologyTrackingId, bloodDrawDate });
    };

    // Closing before the PUT is what stops a second click sending a second email.
    const handleConfirmCorrection = () => {
        const correction = pendingCorrection;

        setPendingCorrection(null);
        updateBloodDrawDate(correction.serologyTrackingId, correction.bloodDrawDate);
    };

    return (
        <>
            {props.isCenterLogin == false && (
                <div style={styles.titleRow}>
                    <div style={styles.title}>Serology Blood Draws Outside 5-7 Day Window</div>
                </div>
            )}

            <Well bsSize="sm" style={styles.filterWell}>
                <div style={styles.filterGroup}>
                    <div style={styles.filterItem}>
                        <div style={styles.filterLabel}>Filter by Surgery Date:</div>
                        <FormControl
                            type="date"
                            bsSize="sm"
                            style={styles.dateInput}
                            value={surgeryDateFrom}
                            onChange={(e: FormEvent<FormControl & HTMLInputElement>) =>
                                setSurgeryDateFrom(e.currentTarget.value)
                            }
                        />
                        <div style={styles.filterLabel}>&nbsp;to&nbsp;</div>
                        <FormControl
                            type="date"
                            bsSize="sm"
                            style={styles.dateInput}
                            value={surgeryDateTo}
                            onChange={(e: FormEvent<FormControl & HTMLInputElement>) =>
                                setSurgeryDateTo(e.currentTarget.value)
                            }
                        />
                    </div>

                    {props.isCenterLogin == false && (
                        <>
                            <div style={styles.filterItem}>
                                <div style={styles.filterLabel}>Donor Center:</div>
                                <FormControl
                                    componentClass="select"
                                    bsSize="sm"
                                    style={styles.centerSelect}
                                    value={donorCenterId}
                                    onChange={(e: FormEvent<FormControl & HTMLSelectElement>) =>
                                        setDonorCenterId(parseInt(e.currentTarget.value))
                                    }
                                >
                                    <option value={0}>- All Centers -</option>
                                    {centerOptions.map((center) => (
                                        <option key={`dc-${center.id}`} value={center.id}>
                                            {center.alias}
                                        </option>
                                    ))}
                                </FormControl>
                            </div>

                            <div style={styles.filterItem}>
                                <div style={styles.filterLabel}>Recipient Center:</div>
                                <FormControl
                                    componentClass="select"
                                    bsSize="sm"
                                    style={styles.centerSelect}
                                    value={recipientCenterId}
                                    onChange={(e: FormEvent<FormControl & HTMLSelectElement>) =>
                                        setRecipientCenterId(parseInt(e.currentTarget.value))
                                    }
                                >
                                    <option value={0}>- All Centers -</option>
                                    {centerOptions.map((center) => (
                                        <option key={`rc-${center.id}`} value={center.id}>
                                            {center.alias}
                                        </option>
                                    ))}
                                </FormControl>
                            </div>
                        </>
                    )}

                    <Button bsStyle="primary" bsSize="sm" disabled={loading} onClick={handleSearch}>
                        Search
                    </Button>
                </div>

                <Button
                    bsSize="sm"
                    disabled={list.length == 0}
                    onClick={exportBloodDrawsOutsideWindow}
                >
                    Export to CSV
                </Button>
            </Well>

            {error != "" && (
                <Alert bsStyle="danger" bsSize="sm" style={styles.errorAlert}>
                    {error}
                </Alert>
            )}

            {loading && <p>Loading...</p>}

            <div style={loading ? styles.gridHidden : null}>
                <Grid
                    data={list}
                    isEditable={props.isCenterLogin == false}
                    onBloodDrawDateSelected={handleBloodDrawDateSelected}
                    onGridReady={onGridReady}
                />
            </div>

            <BloodDrawDateConfirmModal
                show={pendingCorrection != null}
                bloodDrawDate={
                    pendingCorrection != null
                        ? moment(pendingCorrection.bloodDrawDate, dateFormat).format(
                              displayDateFormat,
                          )
                        : ""
                }
                onConfirm={handleConfirmCorrection}
                onCancel={() => setPendingCorrection(null)}
            />
        </>
    );
};

export default LateBloodDraws;
